import { ExternalLink, RotateCcw } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

import {
  PAYMENT_METHODS,
  PRODUCTS,
  formatLongDate,
  labelOf,
} from "../gulaConstants";
import { fullName } from "../gulaTypes";
import { Field, SimpleSelect } from "../SimpleSelect";
import { useAgents } from "../useAgents";
import {
  computeProfit,
  feeForMethod,
  type CostSettings,
  type CostSheet,
} from "./profit";
import { ProfitBreakdown } from "./ProfitBreakdown";
import type { ProfitRow } from "./useMoneyData";

type NumberKey = Exclude<keyof CostSheet, "other_costs_note" | "notes">;
type FormState = Record<NumberKey, string> & {
  other_costs_note: string;
  notes: string;
  payment_method: string;
};

const NUMBER_KEYS: NumberKey[] = [
  "sale_gross",
  "vat_rate",
  "fee_pct",
  "fee_fixed",
  "equipment_cost",
  "faucet_cost",
  "installer_cost",
  "faucet_install_cost",
  "shipping_cost",
  "other_costs",
  "recurring_gross_month",
  "recurring_cost_month",
  "fee_recurring_pct",
  "fee_recurring_fixed",
];

const toForm = (sheet: CostSheet, paymentMethod: string): FormState => ({
  ...(Object.fromEntries(
    NUMBER_KEYS.map((key) => [key, String(sheet[key] ?? 0)]),
  ) as Record<NumberKey, string>),
  other_costs_note: sheet.other_costs_note ?? "",
  notes: sheet.notes ?? "",
  payment_method: paymentMethod,
});

const parse = (value: string) => {
  const parsed = Number(value.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

const toSheet = (form: FormState): CostSheet => ({
  ...(Object.fromEntries(
    NUMBER_KEYS.map((key) => [key, parse(form[key])]),
  ) as Record<NumberKey, number>),
  other_costs_note: form.other_costs_note.trim() || null,
  notes: form.notes.trim() || null,
});

const NumberField = ({
  label,
  value,
  onChange,
  suffix = "€",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
}) => (
  <Field label={label}>
    <div className="relative">
      <Input
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="pr-8 text-right tabular-nums"
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
        {suffix}
      </span>
    </div>
  </Field>
);

/** Cost sheet of one sale: every cost of this client and the profit it leaves. */
export const ProfitSheet = ({
  row,
  settings,
  onClose,
  onSaved,
}: {
  row: ProfitRow;
  settings: CostSettings;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { meId } = useAgents();
  const { order, record, actuals } = row;
  const initialMethod = order.payment_method ?? "card";
  const [form, setForm] = useState<FormState>(() =>
    toForm(row.sheet, initialMethod),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(toForm(row.sheet, order.payment_method ?? "card"));
    // Reload only when another sale is opened, never while typing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  const set = (key: keyof FormState) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const changeMethod = (method: string) => {
    const fee = feeForMethod(method, settings);
    setForm((current) => ({
      ...current,
      payment_method: method,
      fee_pct: String(fee.pct),
      fee_fixed: String(fee.fixed),
    }));
  };

  const resetToDefaults = () => {
    const fee = feeForMethod(form.payment_method, settings);
    setForm((current) => ({
      ...toForm(
        { ...row.defaults, fee_pct: fee.pct, fee_fixed: fee.fixed },
        current.payment_method,
      ),
      notes: current.notes,
    }));
  };

  const sheet = toSheet(form);
  const result = computeProfit(sheet, actuals);
  const isThreeWay = order.product === "blua_three_way_tap";

  const handleSave = async () => {
    setSaving(true);
    try {
      const { shipping_cost, ...costs } = sheet;
      const data = {
        ...costs,
        order_id: order.id,
        updated_by: meId ?? null,
        updated_at: new Date().toISOString(),
      };
      if (record) {
        await dataProvider.update("gula_order_costs", {
          id: record.id,
          data,
          previousData: record,
        });
      } else {
        await dataProvider.create("gula_order_costs", { data });
      }
      await dataProvider.update("gula_orders", {
        id: order.id,
        data: { shipping_cost, payment_method: form.payment_method },
        previousData: order,
      });
      notify("Costes guardados", { type: "success" });
      onSaved();
    } catch (error) {
      console.error(error);
      notify("No se han podido guardar los costes", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            {fullName(order)}
            <Badge variant={record ? "default" : "secondary"}>
              {record ? "Revisado" : "Estimado"}
            </Badge>
          </SheetTitle>
          <SheetDescription>
            {labelOf(PRODUCTS, order.product)} · vendido el{" "}
            {formatLongDate(order.paid_at ?? order.created_at)}
            {order.installer_name ? ` · ${order.installer_name}` : ""}
          </SheetDescription>
          <Link
            to={`/clientes?cliente=${order.client_id}`}
            className="flex w-fit items-center gap-1 text-xs text-muted-foreground hover:underline"
          >
            Abrir ficha del cliente <ExternalLink className="h-3 w-3" />
          </Link>
        </SheetHeader>

        <div className="grid gap-6 px-4 pb-6 md:grid-cols-[1fr_minmax(0,20rem)]">
          <div className="flex flex-col gap-5">
            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold">Lo que paga el cliente</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <NumberField
                  label="Precio del equipo (IVA incl.)"
                  value={form.sale_gross}
                  onChange={set("sale_gross")}
                />
                <SimpleSelect
                  label="Forma de pago"
                  value={form.payment_method}
                  onChange={changeMethod}
                  options={PAYMENT_METHODS}
                />
                <NumberField
                  label="Comisión del cobro"
                  value={form.fee_pct}
                  onChange={set("fee_pct")}
                  suffix="%"
                />
                <NumberField
                  label="Comisión fija por cobro"
                  value={form.fee_fixed}
                  onChange={set("fee_fixed")}
                />
                <NumberField
                  label="IVA"
                  value={form.vat_rate}
                  onChange={set("vat_rate")}
                  suffix="%"
                />
              </div>
              {form.payment_method === "rental" ? (
                <p className="text-xs text-muted-foreground">
                  En alquiler el equipo no se cobra de golpe: lo que paga el
                  cliente entra por las cuotas de abajo.
                </p>
              ) : null}
            </section>

            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold">
                Lo que nos cuesta (sin IVA)
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <NumberField
                  label="Equipo BLUA"
                  value={form.equipment_cost}
                  onChange={set("equipment_cost")}
                />
                <NumberField
                  label="Instalación"
                  value={form.installer_cost}
                  onChange={set("installer_cost")}
                />
                {isThreeWay || parse(form.faucet_cost) > 0 ? (
                  <NumberField
                    label="Grifo 3 vías"
                    value={form.faucet_cost}
                    onChange={set("faucet_cost")}
                  />
                ) : null}
                {parse(form.faucet_install_cost) > 0 ? (
                  <NumberField
                    label="Instalación extra del grifo"
                    value={form.faucet_install_cost}
                    onChange={set("faucet_install_cost")}
                  />
                ) : null}
                <NumberField
                  label="Envío (Packlink)"
                  value={form.shipping_cost}
                  onChange={set("shipping_cost")}
                />
                <NumberField
                  label="Otros costes"
                  value={form.other_costs}
                  onChange={set("other_costs")}
                />
                <Field label="Concepto de otros costes">
                  <Input
                    value={form.other_costs_note}
                    placeholder="Reductor de presión, segunda visita…"
                    onChange={(event) =>
                      set("other_costs_note")(event.target.value)
                    }
                  />
                </Field>
              </div>
            </section>

            <section className="flex flex-col gap-3">
              <div>
                <h3 className="text-sm font-semibold">
                  Cuotas: mantenimiento y alquiler
                </h3>
                <p className="text-xs text-muted-foreground">
                  Lo cobrado hasta hoy sale de Stripe:{" "}
                  {actuals.paymentsCount
                    ? `${actuals.paymentsCount} cobros en ${actuals.monthsActive} meses`
                    : "todavía ninguno"}
                  .
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <NumberField
                  label="Cuota al mes (IVA incl.)"
                  value={form.recurring_gross_month}
                  onChange={set("recurring_gross_month")}
                />
                <NumberField
                  label="Filtros y membrana al mes (sin IVA)"
                  value={form.recurring_cost_month}
                  onChange={set("recurring_cost_month")}
                />
                <NumberField
                  label="Comisión de las cuotas"
                  value={form.fee_recurring_pct}
                  onChange={set("fee_recurring_pct")}
                  suffix="%"
                />
                <NumberField
                  label="Comisión fija por cuota"
                  value={form.fee_recurring_fixed}
                  onChange={set("fee_recurring_fixed")}
                />
              </div>
            </section>

            <Field label="Notas">
              <Textarea
                value={form.notes}
                rows={2}
                onChange={(event) => set("notes")(event.target.value)}
              />
            </Field>

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Guardando…" : "Guardar costes"}
              </Button>
              <Button variant="outline" onClick={resetToDefaults}>
                <RotateCcw className="h-4 w-4" />
                Poner costes por defecto
              </Button>
            </div>
          </div>

          <div className="md:sticky md:top-4 md:self-start">
            <ProfitBreakdown sheet={sheet} actuals={actuals} result={result} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
